import { groupItemsByKey, safeJSONParse } from 'utils/data/parse.utils';
import { parseDateTime } from 'utils/dates/date.utils';

/**
 * Prepares one airport's forecast periods for the dated sections inside its accordion.
 * Accepts the API's JSON string or an already parsed list. A bad response for one airport
 * returns no periods for that airport, so the other airports can still be displayed.
 */
export const getAirportDelayDays = (segments) => {
  const parsedSegments = typeof segments === 'string' ? safeJSONParse(segments, []) : segments;
  if (!Array.isArray(parsedSegments)) return [];

  const periods = parsedSegments
    .filter((segment) => segment && typeof segment === 'object' && !Array.isArray(segment))
    .map((segment) => {
      const timestamp = parseDateTime(segment.iso8601);
      const datePart = String(segment.iso8601 ?? '').match(/^(\d{4}-\d{2}-\d{2})T/);
      // Use the calendar date supplied with the forecast, rather than moving it into
      // the viewer's time zone. Local noon keeps date-only headings on that same day.
      const date = timestamp && datePart ? new Date(`${datePart[1]}T12:00:00`) : null;

      if (date && String(segment.iso8601).slice(11, 19) === '00:00:00') {
        // Preserve the existing display convention: a period ending at midnight belongs
        // to the previous forecast day. Adjust our date once; never change the API record.
        date.setDate(date.getDate() - 1);
      }

      return { segment, date, timestamp };
    })
    .sort((firstPeriod, secondPeriod) => {
      // Undated periods stay visible after the dated forecasts.
      if (!firstPeriod.date) return secondPeriod.date ? 1 : 0;
      if (!secondPeriod.date) return -1;

      // Subtracting timestamps places earlier dates first, then earlier periods within a day.
      return (
        firstPeriod.date.getTime() - secondPeriod.date.getTime() ||
        firstPeriod.timestamp.getTime() - secondPeriod.timestamp.getTime()
      );
    });

  const days = groupItemsByKey(periods, (period) => period.date?.getTime() ?? 'date-unavailable');

  // Keep all periods, even when more than one has the same time-of-day label.
  return Array.from(days, ([key, dayPeriods]) => ({
    key,
    date: dayPeriods[0].date,
    segments: dayPeriods.map((period) => period.segment),
  }));
};
